import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

function TractForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial || { title: "", description: "", price: 0, image_url: "", active: true });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ ...f, price: parseFloat(f.price) || 0 }); }} className="space-y-3">
      <div><Label>Title</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="tract-form-title" /></div>
      <div><Label>Description</Label><Textarea required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="tract-form-desc" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Price (USD)</Label><Input type="number" step="0.01" min="0" required value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="tract-form-price" /></div>
        <div><Label>Image URL</Label><Input value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} data-testid="tract-form-image" /></div>
      </div>
      <div className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="tract-form-active" /><Label>Active</Label></div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="tract-form-save">{saving ? "Saving…" : "Save"}</Button>
      </div>
    </form>
  );
}

function AffiliateForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial || { title: "", description: "", url: "", image_url: "", active: true });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-3">
      <div><Label>Title</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="aff-form-title" /></div>
      <div><Label>Description</Label><Textarea required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="aff-form-desc" /></div>
      <div><Label>URL</Label><Input type="url" required value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="aff-form-url" /></div>
      <div><Label>Image URL</Label><Input value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} data-testid="aff-form-image" /></div>
      <div className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="aff-form-active" /><Label>Active</Label></div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="aff-form-save">{saving ? "Saving…" : "Save"}</Button>
      </div>
    </form>
  );
}

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function ArticleForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial || { title: "", slug: "", author: "", excerpt: "", body: "", image_url: "", published: true });
  const setTitle = (t) => setF((prev) => ({ ...prev, title: t, slug: prev.slug && initial ? prev.slug : slugify(t) }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-3">
      <div><Label>Title</Label><Input required value={f.title} onChange={(e) => setTitle(e.target.value)} data-testid="article-form-title" /></div>
      <div><Label>Slug</Label><Input required value={f.slug} onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })} data-testid="article-form-slug" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Author</Label><Input value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} data-testid="article-form-author" /></div>
        <div><Label>Image URL</Label><Input value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} data-testid="article-form-image" /></div>
      </div>
      <div><Label>Excerpt</Label><Textarea value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} data-testid="article-form-excerpt" /></div>
      <div><Label>Body</Label><Textarea required rows={10} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="article-form-body" /></div>
      <div className="flex items-center gap-2"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="article-form-published" /><Label>Published</Label></div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="article-form-save">{saving ? "Saving…" : "Save"}</Button>
      </div>
    </form>
  );
}

function PlanForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial || { title: "", description: "", duration_days: 7, image_url: "", active: true, days: [] });
  const addDay = () => {
    const nextDay = (f.days?.length ? Math.max(...f.days.map((d) => d.day)) : 0) + 1;
    setF({ ...f, days: [...(f.days || []), { day: nextDay, reference: "", note: "" }] });
  };
  const updDay = (idx, patch) => setF({ ...f, days: f.days.map((d, i) => (i === idx ? { ...d, ...patch } : d)) });
  const rmDay = (idx) => setF({ ...f, days: f.days.filter((_, i) => i !== idx) });

  return (
    <form onSubmit={(e) => {
      e.preventDefault();
      onSave({
        ...f,
        duration_days: parseInt(f.duration_days, 10) || 1,
        days: (f.days || []).map((d) => ({ ...d, day: parseInt(d.day, 10) })),
      });
    }} className="space-y-3">
      <div><Label>Title</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="plan-form-title" /></div>
      <div><Label>Description</Label><Textarea required value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="plan-form-desc" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Duration (days)</Label><Input type="number" min={1} required value={f.duration_days} onChange={(e) => setF({ ...f, duration_days: e.target.value })} data-testid="plan-form-duration" /></div>
        <div><Label>Image URL</Label><Input value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} data-testid="plan-form-image" /></div>
      </div>
      <div className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="plan-form-active" /><Label>Active</Label></div>

      <div className="border-t border-stone-200 pt-3">
        <div className="flex items-center justify-between mb-2">
          <Label>Daily readings</Label>
          <Button type="button" variant="outline" size="sm" onClick={addDay} data-testid="plan-form-add-day">+ Day</Button>
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {(f.days || []).map((d, i) => (
            <div key={i} className="grid grid-cols-[60px_1fr_1fr_auto] gap-2 items-start">
              <Input type="number" value={d.day} onChange={(e) => updDay(i, { day: e.target.value })} placeholder="Day" data-testid={`plan-day-${i}-num`} />
              <Input value={d.reference} onChange={(e) => updDay(i, { reference: e.target.value })} placeholder="e.g. John 1" required data-testid={`plan-day-${i}-ref`} />
              <Input value={d.note} onChange={(e) => updDay(i, { note: e.target.value })} placeholder="Note (optional)" data-testid={`plan-day-${i}-note`} />
              <Button type="button" variant="ghost" size="sm" onClick={() => rmDay(i)} data-testid={`plan-day-${i}-remove`}><Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} /></Button>
            </div>
          ))}
          {(!f.days || f.days.length === 0) && <div className="text-xs text-stone-500">No daily readings yet. Click "+ Day" to add.</div>}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="plan-form-save">{saving ? "Saving…" : "Save"}</Button>
      </div>
    </form>
  );
}

function VideoForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial || { title: "", description: "", youtube_url: "", thumbnail_url: "", active: true });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-3">
      <div><Label>Title</Label><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="video-form-title" /></div>
      <div>
        <Label>YouTube URL</Label>
        <Input required value={f.youtube_url} onChange={(e) => setF({ ...f, youtube_url: e.target.value })}
          placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..." data-testid="video-form-url" />
        <p className="text-xs text-stone-500 mt-1">Paste any YouTube link — regular, short, embed, or shorts.</p>
      </div>
      <div>
        <Label>Custom thumbnail URL (optional)</Label>
        <Input value={f.thumbnail_url} onChange={(e) => setF({ ...f, thumbnail_url: e.target.value })}
          placeholder="Leave blank to use YouTube's default thumbnail" data-testid="video-form-thumb" />
      </div>
      <div><Label>Description (optional)</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="video-form-desc" /></div>
      <div className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="video-form-active" /><Label>Active</Label></div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="video-form-save">{saving ? "Saving…" : "Save"}</Button>
      </div>
    </form>
  );
}

const STATUSES = ["pending", "processing", "shipped", "completed", "cancelled"];

export default function AdminDashboard() {
  const [tab, setTab] = useState("tracts");
  const [tracts, setTracts] = useState([]);
  const [affs, setAffs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [articles, setArticles] = useState([]);
  const [plans, setPlans] = useState([]);
  const [videos, setVideos] = useState([]);
  const [dialog, setDialog] = useState(null); // { type: 'tract'|'aff'|'article'|'plan'|'video', item?: obj }
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [t, a, o, ar, pl, vd] = await Promise.all([
        api.get("/tracts?include_inactive=true"),
        api.get("/affiliates?include_inactive=true"),
        api.get("/orders"),
        api.get("/articles?include_unpublished=true"),
        api.get("/reading-plans?include_inactive=true"),
        api.get("/videos?include_inactive=true"),
      ]);
      setTracts(t.data); setAffs(a.data); setOrders(o.data); setArticles(ar.data); setPlans(pl.data); setVideos(vd.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { loadAll(); }, []);

  const saveTract = async (f) => {
    setSaving(true);
    try {
      if (dialog.item) await api.put(`/tracts/${dialog.item.id}`, f);
      else await api.post("/tracts", f);
      toast.success("Tract saved");
      setDialog(null); loadAll();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const deleteTract = async (id) => {
    if (!window.confirm("Delete this tract?")) return;
    try { await api.delete(`/tracts/${id}`); toast.success("Deleted"); loadAll(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveAff = async (f) => {
    setSaving(true);
    try {
      if (dialog.item) await api.put(`/affiliates/${dialog.item.id}`, f);
      else await api.post("/affiliates", f);
      toast.success("Resource saved");
      setDialog(null); loadAll();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const deleteAff = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try { await api.delete(`/affiliates/${id}`); toast.success("Deleted"); loadAll(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeStatus = async (orderId, status) => {
    try { await api.put(`/orders/${orderId}/status`, null, { params: { status } }); toast.success("Status updated"); loadAll(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveArticle = async (f) => {
    setSaving(true);
    try {
      if (dialog.item) await api.put(`/articles/${dialog.item.id}`, f);
      else await api.post("/articles", f);
      toast.success("Article saved");
      setDialog(null); loadAll();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/articles/${id}`); toast.success("Deleted"); loadAll(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const savePlan = async (f) => {
    setSaving(true);
    try {
      if (dialog.item) await api.put(`/reading-plans/${dialog.item.id}`, f);
      else await api.post("/reading-plans", f);
      toast.success("Reading plan saved");
      setDialog(null); loadAll();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const deletePlan = async (id) => {
    if (!window.confirm("Delete this reading plan?")) return;
    try { await api.delete(`/reading-plans/${id}`); toast.success("Deleted"); loadAll(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveVideo = async (f) => {
    setSaving(true);
    try {
      if (dialog.item) await api.put(`/videos/${dialog.item.id}`, f);
      else await api.post("/videos", f);
      toast.success("Video saved");
      setDialog(null); loadAll();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const deleteVideo = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try { await api.delete(`/videos/${id}`); toast.success("Deleted"); loadAll(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fade-in" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-serif text-4xl text-stone-900">Admin</h1>
        <p className="text-stone-600 mt-1">Manage tracts, resources, and orders.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="tracts" data-testid="tab-tracts">Tracts ({tracts.length})</TabsTrigger>
          <TabsTrigger value="articles" data-testid="tab-articles">Articles ({articles.length})</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">Reading Plans ({plans.length})</TabsTrigger>
          <TabsTrigger value="videos" data-testid="tab-videos">Videos ({videos.length})</TabsTrigger>
          <TabsTrigger value="affiliates" data-testid="tab-affiliates">Resources ({affs.length})</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders ({orders.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="tracts" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setDialog({ type: "tract" })} data-testid="add-tract-btn"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add Tract</Button>
          </div>
          <div className="rounded-md border border-stone-200 bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Title</TableHead><TableHead>Price</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {tracts.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-stone-500 py-8">No tracts yet.</TableCell></TableRow>}
                {tracts.map((t) => (
                  <TableRow key={t.id} data-testid={`admin-tract-${t.id}`}>
                    <TableCell className="font-medium">{t.title}</TableCell>
                    <TableCell>${t.price.toFixed(2)}</TableCell>
                    <TableCell><Badge variant={t.active ? "default" : "secondary"}>{t.active ? "Active" : "Hidden"}</Badge></TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setDialog({ type: "tract", item: t })} data-testid={`edit-tract-${t.id}`}><Pencil className="h-4 w-4" strokeWidth={1.5} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteTract(t.id)} data-testid={`delete-tract-${t.id}`}><Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="articles" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setDialog({ type: "article" })} data-testid="add-article-btn"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add Article</Button>
          </div>
          <div className="rounded-md border border-stone-200 bg-white overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Slug</TableHead><TableHead>Author</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {articles.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-stone-500 py-8">No articles yet.</TableCell></TableRow>}
                {articles.map((a) => (
                  <TableRow key={a.id} data-testid={`admin-article-${a.id}`}>
                    <TableCell className="font-medium">{a.title}</TableCell>
                    <TableCell className="text-sm text-stone-500">{a.slug}</TableCell>
                    <TableCell>{a.author || "—"}</TableCell>
                    <TableCell><Badge variant={a.published ? "default" : "secondary"}>{a.published ? "Published" : "Draft"}</Badge></TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setDialog({ type: "article", item: a })} data-testid={`edit-article-${a.id}`}><Pencil className="h-4 w-4" strokeWidth={1.5} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteArticle(a.id)} data-testid={`delete-article-${a.id}`}><Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="plans" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setDialog({ type: "plan" })} data-testid="add-plan-btn"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add Plan</Button>
          </div>
          <div className="rounded-md border border-stone-200 bg-white overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Duration</TableHead><TableHead>Days added</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {plans.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-stone-500 py-8">No plans yet.</TableCell></TableRow>}
                {plans.map((p) => (
                  <TableRow key={p.id} data-testid={`admin-plan-${p.id}`}>
                    <TableCell className="font-medium">{p.title}</TableCell>
                    <TableCell>{p.duration_days} days</TableCell>
                    <TableCell>{(p.days || []).length}</TableCell>
                    <TableCell><Badge variant={p.active ? "default" : "secondary"}>{p.active ? "Active" : "Hidden"}</Badge></TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setDialog({ type: "plan", item: p })} data-testid={`edit-plan-${p.id}`}><Pencil className="h-4 w-4" strokeWidth={1.5} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deletePlan(p.id)} data-testid={`delete-plan-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="videos" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setDialog({ type: "video" })} data-testid="add-video-btn"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add Video</Button>
          </div>
          <div className="rounded-md border border-stone-200 bg-white overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>YouTube ID</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {videos.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-stone-500 py-8">No videos yet.</TableCell></TableRow>}
                {videos.map((v) => (
                  <TableRow key={v.id} data-testid={`admin-video-${v.id}`}>
                    <TableCell className="font-medium">{v.title}</TableCell>
                    <TableCell className="text-sm text-stone-500">{v.youtube_id}</TableCell>
                    <TableCell><Badge variant={v.active ? "default" : "secondary"}>{v.active ? "Active" : "Hidden"}</Badge></TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setDialog({ type: "video", item: v })} data-testid={`edit-video-${v.id}`}><Pencil className="h-4 w-4" strokeWidth={1.5} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteVideo(v.id)} data-testid={`delete-video-${v.id}`}><Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>


        <TabsContent value="affiliates" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setDialog({ type: "aff" })} data-testid="add-aff-btn"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add Resource</Button>
          </div>
          <div className="rounded-md border border-stone-200 bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Title</TableHead><TableHead>URL</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {affs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-stone-500 py-8">No resources yet.</TableCell></TableRow>}
                {affs.map((a) => (
                  <TableRow key={a.id} data-testid={`admin-aff-${a.id}`}>
                    <TableCell className="font-medium">{a.title}</TableCell>
                    <TableCell className="max-w-[280px] truncate"><a href={a.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{a.url}</a></TableCell>
                    <TableCell><Badge variant={a.active ? "default" : "secondary"}>{a.active ? "Active" : "Hidden"}</Badge></TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setDialog({ type: "aff", item: a })} data-testid={`edit-aff-${a.id}`}><Pencil className="h-4 w-4" strokeWidth={1.5} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteAff(a.id)} data-testid={`delete-aff-${a.id}`}><Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <div className="rounded-md border border-stone-200 bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Date</TableHead><TableHead>Tract</TableHead><TableHead>Qty</TableHead><TableHead>Customer</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {orders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-stone-500 py-8">No orders yet.</TableCell></TableRow>}
                {orders.map((o) => (
                  <TableRow key={o.id} data-testid={`admin-order-${o.id}`}>
                    <TableCell className="text-xs text-stone-500">{new Date(o.created_at).toLocaleString()}</TableCell>
                    <TableCell className="font-medium">{o.tract_title}</TableCell>
                    <TableCell>{o.quantity}</TableCell>
                    <TableCell>
                      <div className="text-sm">{o.name}</div>
                      <div className="text-xs text-stone-500">{o.email}</div>
                    </TableCell>
                    <TableCell>${o.total.toFixed(2)}</TableCell>
                    <TableCell>
                      <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                        <SelectTrigger className="h-8 w-[130px]" data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {dialog?.type === "tract" && (dialog?.item ? "Edit tract" : "Add tract")}
              {dialog?.type === "aff" && (dialog?.item ? "Edit resource" : "Add resource")}
              {dialog?.type === "article" && (dialog?.item ? "Edit article" : "Add article")}
              {dialog?.type === "plan" && (dialog?.item ? "Edit reading plan" : "Add reading plan")}
              {dialog?.type === "video" && (dialog?.item ? "Edit video" : "Add video")}
            </DialogTitle>
          </DialogHeader>
          {dialog?.type === "tract" && <TractForm initial={dialog.item} onSave={saveTract} onCancel={() => setDialog(null)} saving={saving} />}
          {dialog?.type === "aff" && <AffiliateForm initial={dialog.item} onSave={saveAff} onCancel={() => setDialog(null)} saving={saving} />}
          {dialog?.type === "article" && <ArticleForm initial={dialog.item} onSave={saveArticle} onCancel={() => setDialog(null)} saving={saving} />}
          {dialog?.type === "plan" && <PlanForm initial={dialog.item} onSave={savePlan} onCancel={() => setDialog(null)} saving={saving} />}
          {dialog?.type === "video" && <VideoForm initial={dialog.item} onSave={saveVideo} onCancel={() => setDialog(null)} saving={saving} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
